import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import {
  CALL_MINI_IFRAME_REF_HEIGHT,
  CALL_MINI_IFRAME_REF_WIDTH,
  CALL_MINI_WINDOW_DEFAULT_HEIGHT,
  CALL_MINI_WINDOW_DEFAULT_WIDTH,
  CALL_MINI_WINDOW_HEADER_EST,
  CALL_MINI_WINDOW_MIN_HEIGHT,
  CALL_MINI_WINDOW_MIN_WIDTH,
  CALL_MINI_WINDOW_STORAGE_KEY,
  CALL_MINI_WINDOW_VIEWPORT_MARGIN,
  CALL_SPLIT_DEFAULT_MOBILE_RATIO,
  CALL_SPLIT_DEFAULT_RATIO,
  CALL_SPLIT_MAX_RATIO,
  CALL_SPLIT_MIN_RATIO,
  CALL_SPLIT_MOBILE_STORAGE_KEY,
  CALL_SPLIT_STORAGE_KEY,
} from "../lib/board/callAppUrl";

const MOBILE_MQ = "(max-width: 720px)";

export type CallPanelView = "closed" | "docked" | "minimized" | "hidden";

export type CallMiniWindowRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function clampCallRatio(ratio: number): number {
  return Math.min(CALL_SPLIT_MAX_RATIO, Math.max(CALL_SPLIT_MIN_RATIO, ratio));
}

function readStoredRatio(mobile: boolean): number {
  const key = mobile ? CALL_SPLIT_MOBILE_STORAGE_KEY : CALL_SPLIT_STORAGE_KEY;
  const fallback = mobile ? CALL_SPLIT_DEFAULT_MOBILE_RATIO : CALL_SPLIT_DEFAULT_RATIO;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    return clampCallRatio(n);
  } catch {
    return fallback;
  }
}

function defaultMiniWindowRect(): CallMiniWindowRect {
  if (typeof window === "undefined") {
    return {
      x: 16,
      y: 16,
      width: CALL_MINI_WINDOW_DEFAULT_WIDTH,
      height: CALL_MINI_WINDOW_DEFAULT_HEIGHT,
    };
  }
  const width = CALL_MINI_WINDOW_DEFAULT_WIDTH;
  const height = CALL_MINI_WINDOW_DEFAULT_HEIGHT;
  const margin = CALL_MINI_WINDOW_VIEWPORT_MARGIN;
  return {
    x: Math.max(margin, window.innerWidth - width - margin),
    y: Math.max(margin, window.innerHeight - height - margin),
    width,
    height,
  };
}

function clampMiniWindowRect(rect: CallMiniWindowRect): CallMiniWindowRect {
  if (typeof window === "undefined") return rect;
  const margin = CALL_MINI_WINDOW_VIEWPORT_MARGIN;
  const maxWidth = Math.max(
    CALL_MINI_WINDOW_MIN_WIDTH,
    window.innerWidth - margin * 2
  );
  const maxHeight = Math.max(
    CALL_MINI_WINDOW_MIN_HEIGHT,
    window.innerHeight - margin * 2
  );
  const width = Math.min(maxWidth, Math.max(CALL_MINI_WINDOW_MIN_WIDTH, rect.width));
  const height = Math.min(maxHeight, Math.max(CALL_MINI_WINDOW_MIN_HEIGHT, rect.height));
  const maxX = window.innerWidth - width - margin;
  const maxY = window.innerHeight - height - margin;
  return {
    width,
    height,
    x: Math.min(maxX, Math.max(margin, rect.x)),
    y: Math.min(maxY, Math.max(margin, rect.y)),
  };
}

function readStoredMiniWindowRect(): CallMiniWindowRect {
  try {
    const raw = localStorage.getItem(CALL_MINI_WINDOW_STORAGE_KEY);
    if (!raw) return defaultMiniWindowRect();
    const parsed = JSON.parse(raw) as Partial<CallMiniWindowRect>;
    if (
      typeof parsed.x !== "number" ||
      typeof parsed.y !== "number" ||
      typeof parsed.width !== "number" ||
      typeof parsed.height !== "number"
    ) {
      return defaultMiniWindowRect();
    }
    return clampMiniWindowRect(parsed as CallMiniWindowRect);
  } catch {
    return defaultMiniWindowRect();
  }
}

function persistMiniWindowRect(rect: CallMiniWindowRect): void {
  try {
    localStorage.setItem(CALL_MINI_WINDOW_STORAGE_KEY, JSON.stringify(rect));
  } catch {
    /* ignore */
  }
}

function measureMiniHeaderHeight(shellRef: RefObject<HTMLElement | null>): number {
  const header = shellRef.current?.querySelector<HTMLElement>(".wb-board-call-shell__header");
  const height = header?.getBoundingClientRect().height;
  if (height && height > 18) return height;
  return CALL_MINI_WINDOW_HEADER_EST;
}

/** Подогнать размер окна ровно под масштабированную звонилку (без чёрных полей). */
export function fitMiniWindowToContent(
  rect: CallMiniWindowRect,
  headerHeight: number
): CallMiniWindowRect {
  const bodyW = Math.max(1, rect.width);
  const bodyH = Math.max(1, rect.height - headerHeight);
  const scale = Math.min(
    bodyW / CALL_MINI_IFRAME_REF_WIDTH,
    bodyH / CALL_MINI_IFRAME_REF_HEIGHT,
    1
  );
  const contentW = Math.ceil(CALL_MINI_IFRAME_REF_WIDTH * scale);
  const contentH = Math.ceil(CALL_MINI_IFRAME_REF_HEIGHT * scale);
  return clampMiniWindowRect({
    ...rect,
    width: contentW,
    height: contentH + headerHeight,
  });
}

export function useBoardCallSplit(
  containerRef: RefObject<HTMLElement | null>,
  callShellRef: RefObject<HTMLElement | null>
) {
  const [callView, setCallView] = useState<CallPanelView>("closed");
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_MQ).matches : false
  );
  const [callRatio, setCallRatio] = useState(() => readStoredRatio(false));
  const [mobileCallRatio, setMobileCallRatio] = useState(() => readStoredRatio(true));
  const [miniWindowRect, setMiniWindowRect] = useState<CallMiniWindowRect>(() =>
    readStoredMiniWindowRect()
  );
  const callRatioRef = useRef(callRatio);
  const mobileCallRatioRef = useRef(mobileCallRatio);
  const miniWindowRectRef = useRef(miniWindowRect);
  callRatioRef.current = callRatio;
  mobileCallRatioRef.current = mobileCallRatio;
  miniWindowRectRef.current = miniWindowRect;

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobileLayout(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const onResize = () => {
      setMiniWindowRect((prev) => clampMiniWindowRect(prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const updateMiniWindowRect = useCallback((next: CallMiniWindowRect) => {
    const clamped = clampMiniWindowRect(next);
    miniWindowRectRef.current = clamped;
    setMiniWindowRect(clamped);
    persistMiniWindowRect(clamped);
  }, []);

  const snapMiniWindowToContent = useCallback(() => {
    const headerHeight = measureMiniHeaderHeight(callShellRef);
    updateMiniWindowRect(
      fitMiniWindowToContent(miniWindowRectRef.current, headerHeight)
    );
  }, [callShellRef, updateMiniWindowRect]);

  const toggleOpen = useCallback(() => {
    setCallView((v) => {
      if (v === "closed" || v === "minimized" || v === "hidden") return "docked";
      return v;
    });
  }, []);

  const minimizeCall = useCallback(() => {
    setCallView((v) => (v === "docked" ? "minimized" : v));
  }, []);

  useEffect(() => {
    if (callView !== "minimized") return;
    const id = requestAnimationFrame(() => {
      snapMiniWindowToContent();
    });
    return () => cancelAnimationFrame(id);
  }, [callView, snapMiniWindowToContent]);

  const hideCall = useCallback(() => {
    setCallView((v) => (v === "docked" || v === "minimized" ? "hidden" : v));
  }, []);

  const open = callView === "docked";
  const callActive = callView !== "closed";
  /** Панель свёрнута или скрыта — iframe остаётся смонтированным. */
  const callPanelBackgrounded = callView === "minimized" || callView === "hidden";

  const onHandlePointerDown = useCallback(
    (ev: React.PointerEvent<HTMLDivElement>) => {
      ev.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const handle = ev.currentTarget;
      handle.setPointerCapture(ev.pointerId);
      const mobile = window.matchMedia(MOBILE_MQ).matches;

      const onMove = (moveEv: PointerEvent) => {
        const rect = container.getBoundingClientRect();
        if (rect.width < 8 || rect.height < 8) return;
        const ratio = mobile
          ? (rect.bottom - moveEv.clientY) / rect.height
          : (rect.right - moveEv.clientX) / rect.width;
        const next = clampCallRatio(ratio);
        if (mobile) {
          mobileCallRatioRef.current = next;
          setMobileCallRatio(next);
        } else {
          callRatioRef.current = next;
          setCallRatio(next);
        }
      };

      const onUp = (upEv: PointerEvent) => {
        handle.releasePointerCapture(upEv.pointerId);
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
        try {
          const key = mobile ? CALL_SPLIT_MOBILE_STORAGE_KEY : CALL_SPLIT_STORAGE_KEY;
          const value = mobile ? mobileCallRatioRef.current : callRatioRef.current;
          localStorage.setItem(key, String(value));
        } catch {
          /* ignore */
        }
      };

      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
      handle.addEventListener("pointercancel", onUp);
    },
    [containerRef]
  );

  const onMiniWindowDragPointerDown = useCallback(
    (ev: React.PointerEvent<HTMLElement>) => {
      if ((ev.target as HTMLElement).closest("button, a")) return;
      ev.preventDefault();
      const header = ev.currentTarget;
      header.setPointerCapture(ev.pointerId);
      const startX = ev.clientX;
      const startY = ev.clientY;
      const origin = miniWindowRectRef.current;

      const onMove = (moveEv: PointerEvent) => {
        updateMiniWindowRect({
          ...origin,
          x: origin.x + (moveEv.clientX - startX),
          y: origin.y + (moveEv.clientY - startY),
        });
      };

      const onUp = (upEv: PointerEvent) => {
        header.releasePointerCapture(upEv.pointerId);
        header.removeEventListener("pointermove", onMove);
        header.removeEventListener("pointerup", onUp);
        header.removeEventListener("pointercancel", onUp);
      };

      header.addEventListener("pointermove", onMove);
      header.addEventListener("pointerup", onUp);
      header.addEventListener("pointercancel", onUp);
    },
    [updateMiniWindowRect]
  );

  const onMiniWindowResizePointerDown = useCallback(
    (ev: React.PointerEvent<HTMLDivElement>) => {
      ev.preventDefault();
      ev.stopPropagation();
      const handle = ev.currentTarget;
      handle.setPointerCapture(ev.pointerId);
      const startX = ev.clientX;
      const startY = ev.clientY;
      const origin = miniWindowRectRef.current;

      const onMove = (moveEv: PointerEvent) => {
        const headerHeight = measureMiniHeaderHeight(callShellRef);
        const draft: CallMiniWindowRect = {
          ...origin,
          width: origin.width + (moveEv.clientX - startX),
          height: origin.height + (moveEv.clientY - startY),
        };
        updateMiniWindowRect(fitMiniWindowToContent(draft, headerHeight));
      };

      const onUp = (upEv: PointerEvent) => {
        handle.releasePointerCapture(upEv.pointerId);
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
      };

      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
      handle.addEventListener("pointercancel", onUp);
    },
    [callShellRef, updateMiniWindowRect]
  );

  const activeRatio = isMobileLayout ? mobileCallRatio : callRatio;

  return {
    callView,
    callActive,
    callPanelBackgrounded,
    open,
    setOpen: (next: boolean) => setCallView(next ? "docked" : "closed"),
    setCallView,
    toggleOpen,
    minimizeCall,
    hideCall,
    miniWindowRect,
    snapMiniWindowToContent,
    onMiniWindowDragPointerDown,
    onMiniWindowResizePointerDown,
    callRatio: activeRatio,
    desktopCallRatio: callRatio,
    mobileCallRatio,
    onHandlePointerDown,
    boardRatio: 1 - activeRatio,
    isMobileLayout,
  };
}
